import { Badge, Button, Group, Modal, NumberInput, Select, Stack, Text, TextInput } from '@mantine/core';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { rawApi } from '../../api/client';
import { listKey } from '../../api/queryKeys';
import { CrudSection } from '../../components/CrudSection';
import { useLocationId } from '../../hooks/useSeedLocation';
import { enumOptions } from '../../lib/enums';
import { toMinorUnits } from '../../lib/format';
import { notifyError, notifySaved } from '../../lib/notify';

/**
 * The registers themselves, and the drawers they count money into.
 *
 * There was exactly one register in this business and it came out of a seed file — no screen could
 * add a second, and nothing in the office could name the one that existed. **The bar and the
 * restaurant therefore shared one drawer**, which means an over or a short could never be attributed
 * to a person, and being able to attribute one is most of what counting a drawer off is for. The
 * register will want these ids too when it is built: a terminal opens a drawer session against a
 * REGISTER, so a business that cannot create one cannot put a second terminal on the floor.
 *
 * A register and a cash box are two different things and the difference is the point. The register
 * is the point of sale — the thing a shift is opened against and a shortage is attributed through.
 * The cash box is the physical money. Giving each bar its own of BOTH is what separates the two
 * shortages; giving them separate registers pointed at one drawer would put the names on the same
 * pile of cash again.
 *
 * There is no edit here, and that is the API's shape rather than an omission: registers are created
 * and listed and nothing amends one. Renaming a register would rename it on every drawer session it has
 * ever owned, including the ones an accountant has already read.
 */

const REGISTERS_PATH = '/admin/v1/operations/registers';
const CASH_BOXES_PATH = '/admin/v1/cash/locations';

export function Registers() {
  const locationId = useLocationId();

  return (
    <CrudSection
      title="Registers"
      entityNoun="register"
      description="Each point of sale that opens a drawer. Give the bar and the restaurant one each, pointed at their own cash drawer, and a shortage belongs to whoever counted that drawer in."
      listPath={REGISTERS_PATH}
      createPath={REGISTERS_PATH}
      defaults={{ locationId }}
      rowLabel={(r) => String(r.name ?? 'this register')}
      emptyMessage="No registers yet. Add one for each place a drawer is opened."
      actions={<AddCashBoxButton />}
      columns={[
        { accessor: 'name', title: 'Register' },
        { accessor: 'cashLocationId', title: 'Counts into', reference: 'cashBox' },
        { accessor: 'sharingModel', title: 'Responsibility', enumGroup: 'registerSharing' },
        {
          accessor: 'active',
          title: 'In use',
          render: (r) =>
            r.active === false ? (
              <Badge color="gray" variant="light">
                No longer used
              </Badge>
            ) : (
              <Badge color="teal" variant="light">
                In use
              </Badge>
            ),
        },
      ]}
      fields={[
        {
          name: 'name',
          label: 'What you call it',
          type: 'text',
          required: true,
          description: 'How it is known to staff — "Bar register", "Restaurant register"',
        },
        {
          name: 'cashLocationId',
          label: 'Which drawer it counts into',
          type: 'entity',
          resource: 'cashBox',
          description:
            'The physical drawer. Two registers sharing one drawer cannot have a shortage attributed to either of them — add a drawer of its own with the button above.',
        },
        {
          name: 'sharingModel',
          label: 'Who is responsible for it',
          type: 'select',
          required: true,
          defaultValue: 'ownerAccountable',
          options: enumOptions('registerSharing'),
        },
      ]}
    />
  );
}

/**
 * Adding the physical box, from the screen where a register is asked which one it uses.
 *
 * Deliberately here and not on its own screen: the moment somebody needs a second cash drawer is
 * the moment they are adding the second register, and sending them elsewhere to create one and back
 * again is how a field ends up left empty. The standard float is asked for at the same time because
 * it is what an opening count is measured against — a drawer created without one measures every
 * opening count against nothing.
 */
export function AddCashBoxButton() {
  const locationId = useLocationId();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [kind, setKind] = useState<string | null>('drawer');
  // Whatever the box handed back, string and all — a money field returns its value as a string
  // every time the last decimal is a zero, which for `decimalScale={2}` is every time.
  const [usd, setUsd] = useState<number | string>('');
  const [hnl, setHnl] = useState<number | string>('');

  const create = useMutation({
    mutationFn: () =>
      rawApi('POST', CASH_BOXES_PATH, {
        locationId,
        name: name.trim(),
        kind,
        standardFloatUsdMinor: toMinorUnits(usd) ?? 0,
        standardFloatHnlMinor: toMinorUnits(hnl) ?? 0,
      }),
    onSuccess: () => {
      notifySaved(name.trim());
      void queryClient.invalidateQueries({ queryKey: listKey(CASH_BOXES_PATH) });
      setOpen(false);
      setName('');
      setKind('drawer');
      setUsd('');
      setHnl('');
    },
    onError: (err) => notifyError(err, 'add this cash box'),
  });

  return (
    <>
      <Button size="sm" variant="default" onClick={() => setOpen(true)}>
        Add a cash box
      </Button>
      <Modal
        opened={open}
        onClose={() => setOpen(false)}
        title="Add a cash box"
        size="lg"
        closeOnClickOutside={false}
      >
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            A physical place money sits: a cash drawer, the box of small notes kept back for change, or the
            safe. A register counts into one of these, and a count is measured against the float below.
          </Text>
          <TextInput
            label="What you call it"
            description='How staff refer to it — "Bar drawer", "Restaurant drawer"'
            required
            value={name}
            onChange={(e) => setName(e.currentTarget.value)}
          />
          <Select
            label="What it is"
            data={enumOptions('cashBoxKind')}
            value={kind}
            onChange={setKind}
            allowDeselect={false}
          />
          <Group grow align="flex-start">
            <NumberInput
              label="Standard float — dollars"
              description="What it should start a shift holding"
              prefix="$ "
              decimalScale={2}
              fixedDecimalScale
              value={usd}
              onChange={setUsd}
            />
            <NumberInput
              label="Standard float — lempiras"
              /*
               * This promised that changing it later "never touches a drawer already open", and for
               * one figure out of three that was false: the expected drop was recomputed against
               * this row every time the panel was opened, on a shift still trading and on one
               * counted off the day before. `cash/drop.ts` now reads the float the shift opened on,
               * so the promise holds and can be made again — see the note in `RegisterPage.tsx`.
               */
              description="Takes effect at the next open — no shift already running or counted off moves"
              prefix="L "
              decimalScale={2}
              fixedDecimalScale
              value={hnl}
              onChange={setHnl}
            />
          </Group>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setOpen(false)} disabled={create.isPending}>
              Cancel
            </Button>
            <Button
              loading={create.isPending}
              disabled={!name.trim() || !kind || !locationId}
              onClick={() => create.mutate()}
            >
              Add cash box
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}
